import { Component, DestroyRef, inject, viewChild } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthApiService } from '@api/service/auth.api.service';
import { InvalidResetPasswordTokenError } from '@core/common/errors';
import { CustomValidators, FormDisplayControl } from '@oi-soft/common';
import { VoltaCustomValidators } from '@shared/classes/volta-custom-validators';
import { FormControlUnderlineComponent } from '@shared/components/form/form-control/form-control-types/form-control-underline/form-control-underline.component';
import { ModalFormButtonGroupComponent } from '@shared/components/modals/modal/modal-button-group/modal-form-button-group/modal-form-button-group.component';
import { VoltaSweetAlertService } from '@shared/services/volta-sweet-alert/volta-sweet-alert.service';
import { tap } from 'rxjs';
import { FormControlComponent } from "../../../../shared/components/form/form-control/form-control.component";
import { CascadingModalComponent } from "../../../../shared/components/modals/cascading-modal/cascading-modal.component";
import { ModalComponent } from "../../../../shared/components/modals/modal/modal.component";

@Component({
    selector: 'app-forgot-password',
    imports: [FormControlComponent, ModalComponent, CascadingModalComponent, FormControlUnderlineComponent, ReactiveFormsModule, ModalFormButtonGroupComponent],
    templateUrl: './forgot-password.component.html',
    styleUrl: './forgot-password.component.scss'
})
export class ForgotPasswordComponent {
    
    private swalService = inject(VoltaSweetAlertService);
    private authApiService = inject(AuthApiService);
    private destroyRef = inject(DestroyRef);
    
    cascadingModal = viewChild(CascadingModalComponent);
    
    form = new FormGroup({
        usernameOrEmail: new FormDisplayControl(
            'Email or Username', '',
            {
                validators: [Validators.required],
            }
        ),
        code: new FormDisplayControl(
            'Reset Code', '',
            {
                validators: [Validators.required, CustomValidators.numeric, Validators.minLength(6), Validators.maxLength(6)],
            }
        ),
        passwords: new FormGroup({
            newPassword: new FormDisplayControl(
                'New Password', '',
                {
                    validators: [Validators.required, VoltaCustomValidators.userPassword]
                }
            ),
            confirmPassword: new FormDisplayControl(
                'Confirm Password', '',
                {
                    validators: [Validators.required, CustomValidators.equalToControl("newPassword")]
                }
            )
        })
    });
    
    onRequestEmail(){
        if(this.form.controls.usernameOrEmail.valid){
            this.swalService.fireLoading({message:`Sending email to account ${this.form.controls.usernameOrEmail.value}, please wait...`})
            
            //authenticate
            const subscription = this.authApiService.Auth.ForgotPassword.Post({
                body: {
                    usernameOrEmail: this.form.controls.usernameOrEmail.value
                }
            })
            .pipe(tap(this.swalService.closeLoadingPreSubscribe()))
            .subscribe({
                next: (response) => {
                    this.cascadingModal()?.next()
                },
                error: (error) => {
                    this.swalService.fireError(error);
                }
            });
            
            this.destroyRef.onDestroy(() => {
                subscription.unsubscribe();
            })
            
        }
        else{
            
            Object.keys(this.form.controls).forEach(key => {
                this.form.get(key)!.markAsDirty();
                this.form.get(key)!.markAsTouched();
            });
            
        }
    }
    
    onSubmit(){
        if(this.form.valid){
            
            this.swalService.fireLoading({message:`Changing Password of ${this.form.controls.usernameOrEmail.value}, please wait...`})
            
            //authenticate
            const subscription = this.authApiService.Auth.ResetPassword.Post({
                body:{
                    usernameOrEmail: this.form.controls.usernameOrEmail.value,
                    resetCode: this.form.controls.code.value,
                    newPassword: this.form.controls.passwords.controls.confirmPassword.value
                }
            })
            .pipe(tap(this.swalService.closeLoadingPreSubscribe()))
            .subscribe({
                next: (response) => {
                    this.swalService.fireSuccess({message: "Password changed!", timer: true});
                    this.form.reset();
                    this.cascadingModal()?.end();
                },
                error: (error) => {
                    this.swalService.fireError(error);
                    
                    if(error instanceof InvalidResetPasswordTokenError){
                        
                        //clean passwords
                        Object.keys(this.form.controls.passwords.controls).forEach(key => {
                            this.form.controls.passwords.get(key)!.markAsUntouched();
                            this.form.controls.passwords.get(key)!.markAsPristine();
                            this.form.controls.passwords.get(key)!.setValue('');
                        });
                        
                        this.cascadingModal()?.back();
                    }
                }
            });
            
            this.destroyRef.onDestroy(() => {
                subscription.unsubscribe();
            })
            
        }
        else{
            
            Object.keys(this.form.controls).forEach(key => {
                this.form.get(key)!.markAsDirty();
                this.form.get(key)!.markAsTouched();
            });
            
        }
    }
    
}
